import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, ValidationErrors } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { ACTIVITY_FORM_CONFIG, ActivityFormValue } from './activity-form-config';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';

// NOT PROVIDED IN ROOT
@Injectable()
export class ActivityFormService {
	private formSubject = new BehaviorSubject<FormGroup>(this.createEmptyForm());

	public form$: Observable<FormGroup> = this.formSubject.asObservable();

	constructor (private fb: FormBuilder) {}

	private createEmptyForm (): FormGroup {
		const formControls: { [key: string]: FormControl } = {};

		Object.keys(ACTIVITY_FORM_CONFIG).forEach((key) => {
			const config = ACTIVITY_FORM_CONFIG[key];
			formControls[key] = new FormControl(config.defaultValue, config.validators);
		});

		return this.fb.group(formControls);
	}

	public initializeForm (activity: ActivityModel): FormGroup {
		const form = this.getForm();

		Object.keys(ACTIVITY_FORM_CONFIG).forEach((key) => {
			const config = ACTIVITY_FORM_CONFIG[key];
			let value = null;

			if (activity.hasOwnProperty(key)) {
				const isTypeMatch = config.type === 'array'
					? Array.isArray(activity[key])
					: typeof activity[key] === config.type;

				if (isTypeMatch) {
					value = activity[key];
				} else {
					value = config.defaultValue;
				}
			} else {
				value = config.defaultValue;
			}

			const control = form.get(key);

			if (control) {
				control.setValue(value);
			}
		});

		this.formSubject.next(form);
		return form;
	}

	public getForm (): FormGroup {
		return this.formSubject.value;
	}

	public getFormValue (): ActivityFormValue {
		return this.getForm().value;
	}

	public isFormValid (): boolean {
		return this.getForm().valid;
	}

	public isFormDirty (): boolean {
		return this.getForm().dirty;
	}

	public markFormAsDirty (): void {
		this.getForm().markAsDirty();
	}

	public markFormAsPristine (): void {
		this.getForm().markAsPristine();
	}

	public resetForm (): void {
		const form = this.createEmptyForm();
		this.formSubject.next(form);
	}

	public getControl (controlName: string): FormControl | null {
		return this.getForm().get(controlName) as FormControl;
	}

	public isControlValid (controlName: string): boolean {
		const control = this.getControl(controlName);
		return control ? control.valid : false;
	}

	public isControlDirty (controlName: string): boolean {
		const control = this.getControl(controlName);
		return control ? control.dirty : false;
	}

	public getControlErrors (controlName: string): ValidationErrors | null {
		const control = this.getControl(controlName);
		return control ? control.errors : null;
	}

	public markControlAsDirty (controlName: string): void {
		const control = this.getControl(controlName);
		if (control) {
			control.markAsDirty();
		}
	}

	public markControlAsPristine (controlName: string): void {
		const control = this.getControl(controlName);
		if (control) {
			control.markAsPristine();
		}
	}

	public setControlValidators (controlName: string, validators: any[] | null): void {
		const control = this.getControl(controlName);
		if (control) {
			control.setValidators(validators);
			control.updateValueAndValidity();
		}
	}

	public clearControlValidators (controlName: string): void {
		const control = this.getControl(controlName);
		if (control) {
			control.clearValidators();
			control.updateValueAndValidity();
		}
	}
}
